const PROJECT_PATH_EXTENSION = /\.(?:md|txt|json)$/iu;
const INVISIBLE_PATH_CHARS = /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/gu;
const WRAPPED_QUOTE_CHARS = /^[`"'“”‘’]+|[`"'“”‘’]+$/gu;
const TRAILING_PATH_PUNCTUATION = /[),!?]+$/gu;

const getProjectFileName = (path: string) =>
  path.includes("/") ? path.slice(path.lastIndexOf("/") + 1) : path;

export const extractChapterReferenceTokens = (message: string) => {
  if (typeof message !== "string" || message.length === 0) {
    return [];
  }

  const seen = new Set<string>();
  const results: string[] = [];
  for (const match of message.matchAll(
    /(?:^|[\s([{])((?:chapter|פרק)\s+0*\d{1,4})(?=$|[\s)\]},.!?])/giu,
  )) {
    const token = match[1]?.trim();
    if (!token) continue;
    const key = token.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    results.push(token);
  }
  return results;
};

export const sanitizeProjectPathCandidate = (value: string) => {
  if (typeof value !== "string") return "";

  let normalized = value.replace(INVISIBLE_PATH_CHARS, "").trim();
  normalized = normalized.replace(TRAILING_PATH_PUNCTUATION, "").trim();

  if (normalized.startsWith("@{") && normalized.endsWith("}")) {
    normalized = normalized.slice(2, -1);
  } else if (normalized.startsWith("@")) {
    normalized = normalized.slice(1);
  }

  normalized = normalized.replace(WRAPPED_QUOTE_CHARS, "").trim();

  return normalized.replace(/\\/g, "/").normalize("NFC");
};

const stripProjectFileExtension = (value: string) =>
  value.replace(PROJECT_PATH_EXTENSION, "");

const normalizeLoosePathName = (value: string) =>
  stripProjectFileExtension(sanitizeProjectPathCandidate(value))
    .toLowerCase()
    .replace(/[_-]+/gu, " ")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();

const extractChapterNumber = (value: string) => {
  const normalized = normalizeLoosePathName(value);
  const match = normalized.match(/(?:^|\s)(?:chapter|פרק)\s+0*(\d{1,4})(?=$|\s)/u);
  return match ? Number.parseInt(match[1], 10) : null;
};

const collectPath = (rawPath: string, seen: Set<string>, results: string[]) => {
  const path = sanitizeProjectPathCandidate(rawPath);
  if (!path || !PROJECT_PATH_EXTENSION.test(path) || seen.has(path)) {
    return;
  }
  seen.add(path);
  results.push(path);
};

export const extractExplicitProjectPaths = (message: string) => {
  if (typeof message !== "string" || message.length === 0) {
    return [];
  }

  const results: string[] = [];
  const seen = new Set<string>();

  for (const match of message.matchAll(/@\{([^}\n]+)\}/gu)) {
    collectPath(match[1] || "", seen, results);
  }

  for (const match of message.matchAll(
    /@((?:[\p{L}\p{N}_. -]+\/)+[\p{L}\p{N}_. -]+\.(?:md|txt|json)|[\p{L}\p{N}_. -]+\.(?:md|txt|json))(?=$|[\s),!?}])/gu,
  )) {
    collectPath(match[1] || "", seen, results);
  }

  return results;
};

export const findCanonicalProjectPath = (
  rawPath: string,
  knownPaths: readonly string[],
) => {
  const normalizedPath = sanitizeProjectPathCandidate(rawPath);
  if (!normalizedPath) return null;

  for (const path of knownPaths) {
    if (sanitizeProjectPathCandidate(path) === normalizedPath) {
      return path;
    }
  }

  if (normalizedPath.includes("/")) {
    return null;
  }

  const looseName = normalizeLoosePathName(normalizedPath);
  if (looseName) {
    const looseMatches = knownPaths.filter((path) => {
      const fileName = getProjectFileName(path);
      return normalizeLoosePathName(fileName) === looseName;
    });
    if (looseMatches.length === 1) return looseMatches[0];
  }

  const chapterNumber = extractChapterNumber(normalizedPath);
  if (chapterNumber !== null) {
    const chapterMatches = knownPaths.filter((path) => {
      if (!path.startsWith("Chapters/")) return false;
      return extractChapterNumber(getProjectFileName(path)) === chapterNumber;
    });
    if (chapterMatches.length === 1) return chapterMatches[0];
  }

  const matchingNames = knownPaths.filter(
    (path) => sanitizeProjectPathCandidate(getProjectFileName(path)) === normalizedPath,
  );

  return matchingNames.length === 1 ? matchingNames[0] : null;
};
